import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import InfoIcon from "@mui/icons-material/Info";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import AlertComponent from "../../subcomponents/AlertComponent";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { retailDiscountTypesTooltip } from "../../../constants/TooltipInformation";
import { fieldValidationHelperText } from "../../../constants/validationHelperText";
import { RetailPropertyEnum } from "../../../constants/NavbarList";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const RetailDiscountTypes = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [discountType, setDiscountType] = useState<string | null>(null);
  const [nameError, setNameError] = useState<boolean>(false);
  const [retailDiscountTypes, setRetailDiscountTypes] = useState<any>([]);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const payload = {
    "Discount Type": discountType,
  };

  const validateInput = () => {
    setNameError(false);
    if (!discountType || String(discountType).trim() === "") {
      setNameError(true);
      return false;
    }
    if (
      !validateString(
        discountType,
        validationLength.discountTypes,
        validationType.SPACE,
      )
    ) {
      setNameError(true);
      return false;
    }
    return true;
  };

  const editRetailDiscountTypes = (list: any) => {
    if (list == null) {
      setSelectedId(null);
      setEditClicked(false);
      setDiscountType(null);
      setNameError(false);
      return;
    }
    if (list !== null) {
      setEditClicked(true);
      if (list.id != null) {
        setSelectedId(list.id);
      } else {
        setSelectedId(null);
      }
    }
    setDiscountType(list?.["Discount Type"] ?? null);
  };

  const updateRetailDiscountTypes = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(
        retailDiscountTypes.filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.DISCOUNTTYPE,
        discountType,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setNameError(false);
    setRetailDiscountTypes((prev: any) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editRetailDiscountTypes(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${RetailPropertyEnum.discountTypes} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addRetailDiscountTypes = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(
        retailDiscountTypes,
        DNDFieldsEnum.DISCOUNTTYPE,
        discountType,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setRetailDiscountTypes((prev: any) =>
      updateData(
        prev !== null && prev?.length
          ? [
              ...prev,
              {
                id: retailDiscountTypes.length + 1,
                ...payload,
              },
            ]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editRetailDiscountTypes(null);
    setEditClicked(false);
  };

  const deleteRetailDiscountTypes = (id: number) => {
    setRetailDiscountTypes(deleteItem(retailDiscountTypes, id));
  };

  useEffect(() => {
    if (fieldValues !== undefined && fieldValues !== null) {
      setRetailDiscountTypes(fieldValues);
    }
  }, []);

  useEffect(() => {
    data(retailDiscountTypes);
    editClicked &&
      setSelectedId(
        checkData(retailDiscountTypes, selectedId) ? selectedId : null,
      );
  }, [retailDiscountTypes]);

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Discount Types
              <Tooltip
                title={tooltipComponent(retailDiscountTypesTooltip.header)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton click={() => editRetailDiscountTypes(null)} />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editRetailDiscountTypes(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Discount Type
              <Tooltip
                title={tooltipComponent(
                  retailDiscountTypesTooltip.discountType,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailDiscountType"
              variant="outlined"
              placeholder="Discount Type"
              value={discountType ?? ""}
              helperText={
                nameError
                  ? !discountType || String(discountType).trim() === ""
                    ? validationMessages.fieldRequired
                    : validationMessages.discountTypes
                  : fieldValidationHelperText.discountType
              }
              error={nameError}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setDiscountType(e.target.value || null);
                setNameError(false);
              }}
            />
          </Stack>

          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={
                selectedId ? updateRetailDiscountTypes : addRetailDiscountTypes
              }
              id="addRetailDiscountType"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Discount
              Type
            </Button>
          </Stack>

          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Discount Types Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {retailDiscountTypes?.length ? (
                <DraggableItemList
                  itemData={retailDiscountTypes}
                  onOrderUpdate={(order) => setRetailDiscountTypes(order)}
                  onEdit={(info) => {
                    editRetailDiscountTypes(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteRetailDiscountTypes(id)}
                  code={DNDFieldsEnum.DISCOUNTTYPE}
                  name={""}
                />
              ) : (
                <Typography variant="body2">
                  No Discount Types Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default RetailDiscountTypes;
